class SaveCartForm extends HTMLElement {
  static klaviyo = Object.freeze({
    companyId: 'RH49u6',
    // Client API hosts, tried in order. The stock AdGuard DNS filter blocks
    // exactly `a.klaviyo.com` (and, per its own comment, deliberately not its
    // subdomains), which is why the form failed on phones running AdGuard while
    // desktops were fine. `fast.a.klaviyo.com` serves the same /client/ API
    // with the same CORS policy (verified 2026-09-07: preflight 200 with
    // `access-control-allow-origin: *`, a live POST /client/subscriptions/
    // returned 202). Undocumented alias — a fallback, never the primary.
    hosts: Object.freeze(['a.klaviyo.com', 'fast.a.klaviyo.com']),
    revision: '2026-01-15',
    source: 'Cart Save',
    signupSource: 'cart_save',
    metricName: 'Saved Cart',
    // Which wording of the consent checkboxes the person saw. Bump when the
    // consent text changes (same convention as popup/bottom-sheet lead forms).
    consentVersion: 'save-cart-v1-2026-09-07',
  });
  static consentLog = Object.freeze({
    // Append-only CNIL consent log (n8n → Supabase email_consent_log); same
    // endpoint the popup/bottom-sheet forms use. Fire-and-forget: its failure
    // must never block or fail the save itself.
    endpoint: 'https://erebor.beglarianfabrics.com/webhook/bf/email-consent',
    source: 'shopify_save_cart',
    timeoutMs: 8000,
  });

  constructor() {
    super();
    this.loading = false;
    this.onSubmitHandler = this.onSubmit.bind(this);
    this.onModalShowHandler = this.onModalShow.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('.save-cart-form__form');
    this.emailInput = this.querySelector('.save-cart-form__email');
    this.consentInput = this.querySelector('.save-cart-form__consent');
    this.trackingInput = this.querySelector('.save-cart-form__tracking');
    this.submitButton = this.querySelector('.save-cart-form__submit');
    this.errorElement = this.querySelector('.save-cart-form__output');
    this.successElement = this.querySelector('.save-cart-form__success');

    if (!this.form || !this.emailInput || !this.consentInput || !this.submitButton) return;

    this.form.addEventListener('submit', this.onSubmitHandler);

    this.modal = this.closest('basic-modal');
    this.modal?.addEventListener('modal:handleAfterShow', this.onModalShowHandler);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmitHandler);
    this.modal?.removeEventListener('modal:handleAfterShow', this.onModalShowHandler);
  }

  get isSuccessShown() {
    return !!this.successElement && !this.successElement.classList.contains('hidden');
  }

  onModalShow() {
    this.resetForm();
    this.focusEmail();
  }

  // Only undo a previous session's terminal state; never touch what a fast
  // user may already have typed before the modal's open transition settled.
  resetForm() {
    if (this.loading) return;
    if (!this.isSuccessShown) return;
    this.form.classList.remove('hidden');
    this.successElement.classList.add('hidden');
    this.setError('');
    this.emailInput.value = '';
    this.emailInput.setCustomValidity('');
    this.consentInput.checked = false;
    this.consentInput.setCustomValidity('');
    if (this.trackingInput) this.trackingInput.checked = false;
  }

  // The shared modal traps focus on its close button. On keyboard/mouse
  // devices move it to the single field instead — unless the person already
  // clicked into the form (then leave their focus alone). Skipped on touch
  // devices so the keyboard doesn't pop up over the modal.
  focusEmail() {
    if (this.isSuccessShown) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (this.form.contains(document.activeElement)) return;
    this.emailInput.focus({ preventScroll: true });
  }

  get listId() {
    return (this.dataset.listId || '').trim();
  }

  get languageIso() {
    return this.dataset.languageIso || '';
  }

  get languageRootUrl() {
    return this.dataset.languageRootUrl || '/';
  }

  validate() {
    this.emailInput.setCustomValidity('');
    this.consentInput.setCustomValidity('');

    if (!this.emailInput.checkValidity()) {
      this.emailInput.setCustomValidity(this.dataset.errorEmail || '');
      this.emailInput.reportValidity();
      return false;
    }

    if (!this.consentInput.checkValidity()) {
      this.consentInput.setCustomValidity(this.dataset.errorTerms || '');
      this.consentInput.reportValidity();
      return false;
    }

    return true;
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.loading) return;

    this.setError('');
    if (!this.validate()) return;

    this.loading = true;
    this.submitButton.classList.add('btn--loading');

    const email = this.emailInput.value.trim();
    const trackingConsent = this.trackingInput?.checked || false;

    let step = 'cart';
    try {
      const cart = await this.fetchCart();
      if (!cart.items?.length) {
        // Cart emptied in another tab since the page rendered — nothing to save.
        throw new Error('cart is empty');
      }
      step = 'subscription';
      if (this.listId) {
        await this.subscribeToKlaviyo(email, trackingConsent);
      } else {
        console.warn('[save-cart-form] no Klaviyo list configured — skipping list subscription, tracking event only');
      }
      step = 'event';
      await Promise.all([
        this.trackSavedCartEvent(email, cart, trackingConsent),
        this.sendConsentLog(email, cart, trackingConsent),
      ]);
      this.onSuccess(cart);
    } catch (error) {
      // A TypeError from fetch means the request never left the browser
      // ("Load failed" / "Failed to fetch"): Safari Private Browsing and
      // content blockers cut a.klaviyo.com (it is on the DuckDuckGo tracker
      // list Safari uses). Tell the person that, not a generic "try again".
      const blocked = step !== 'cart' && SaveCartForm.isNetworkFailure(error);
      console.error(`[save-cart-form] submit failed at step "${step}"${blocked ? ' (request blocked by the browser)' : ''}`, error);
      this.setError((blocked && this.dataset.errorBlocked) || this.dataset.errorGeneric || '');
    } finally {
      this.loading = false;
      this.submitButton.classList.remove('btn--loading');
    }
  }

  static isNetworkFailure(error) {
    return error instanceof TypeError || /load failed|failed to fetch|networkerror/i.test(error?.message || '');
  }

  async fetchCart() {
    const response = await fetch(`${this.languageRootUrl.replace(/\/$/, '')}/cart.js`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js failed with status ${response.status}`);
    return response.json();
  }

  async subscribeToKlaviyo(email, trackingConsent) {
    const attributes = {
      email,
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } },
      },
      properties: {
        source: SaveCartForm.klaviyo.source,
        signup_source: SaveCartForm.klaviyo.signupSource,
        language_iso: this.languageIso,
        language_root_url: this.languageRootUrl,
        email_tracking_consent: trackingConsent,
        consent_version: SaveCartForm.klaviyo.consentVersion,
      },
    };

    const data = {
      type: 'subscription',
      attributes: {
        profile: { data: { type: 'profile', attributes } },
        custom_source: SaveCartForm.klaviyo.source,
      },
      relationships: {
        list: { data: { type: 'list', id: this.listId } },
      },
    };

    const response = await this.postToKlaviyo('/client/subscriptions/', { data });
    if (response.status !== 202 && !response.ok) {
      throw new Error(`Klaviyo subscription failed with status ${response.status}`);
    }
  }

  // Shopify cart permalink: `/cart/<variant>:<qty>,…` re-creates this cart and
  // lands on checkout. Prefixed with the locale root so the checkout opens in
  // the language the person shopped in (`/fr/cart/…`, verified live);
  // `permalinkPath` is the un-prefixed fallback (`cart_permalink_default`).
  //
  // /cart.js reports prices in the presentment currency of the visitor's
  // market. The recovery flow (and the Supabase lead log) branch on a EUR
  // threshold, so also convert the total back to the shop currency via
  // Shopify's storefront rate. Shared by the Klaviyo event and the consent
  // log so the two never compute this differently.
  buildCartMeta(cart) {
    const permalinkPath = `/cart/${(cart.items || []).map((item) => `${item.variant_id}:${item.quantity}`).join(',')}`;
    const cartPermalink = `${this.languageRootUrl.replace(/\/$/, '')}${permalinkPath}`;

    const shopCurrency = this.dataset.shopCurrency || '';
    const rate = parseFloat(window.Shopify?.currency?.rate);
    const totalPriceShopCurrency =
      shopCurrency && cart.currency === shopCurrency
        ? cart.total_price
        : Number.isFinite(rate) && rate > 0
          ? Math.round(cart.total_price / rate)
          : null;

    return { permalinkPath, cartPermalink, shopCurrency, totalPriceShopCurrency };
  }

  async trackSavedCartEvent(email, cart, trackingConsent) {
    const items = (cart.items || []).map((item) => ({
      title: item.product_title,
      variant_title: item.variant_title,
      quantity: item.quantity,
      price: item.price,
      line_price: item.final_line_price ?? item.line_price,
      image: item.image,
      url: item.url,
      handle: item.handle,
      sku: item.sku,
      product_id: item.product_id,
      variant_id: item.variant_id,
    }));

    const { permalinkPath, cartPermalink, shopCurrency, totalPriceShopCurrency } = this.buildCartMeta(cart);

    const data = {
      type: 'event',
      attributes: {
        properties: {
          email,
          cart_token: cart.token || '',
          cart_permalink: cartPermalink,
          cart_permalink_default: permalinkPath,
          item_count: cart.item_count,
          total_price: cart.total_price,
          currency: cart.currency,
          total_price_shop_currency: totalPriceShopCurrency,
          shop_currency: shopCurrency,
          items,
          language_iso: this.languageIso,
          language_root_url: this.languageRootUrl,
          source: SaveCartForm.klaviyo.source,
          email_marketing_consent: true,
          email_tracking_consent: trackingConsent,
          consent_version: SaveCartForm.klaviyo.consentVersion,
        },
        value: Math.round(cart.total_price) / 100,
        value_currency: cart.currency,
        metric: { data: { type: 'metric', attributes: { name: SaveCartForm.klaviyo.metricName } } },
        profile: { data: { type: 'profile', attributes: { email } } },
      },
    };

    const response = await this.postToKlaviyo('/client/events/', { data });
    if (response.status !== 202 && !response.ok) {
      throw new Error(`Klaviyo event tracking failed with status ${response.status}`);
    }
  }

  async sendConsentLog(email, cart, trackingConsent) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SaveCartForm.consentLog.timeoutMs);
    const { cartPermalink, totalPriceShopCurrency } = this.buildCartMeta(cart);

    try {
      await fetch(SaveCartForm.consentLog.endpoint, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          consent_version: SaveCartForm.klaviyo.consentVersion,
          email_tracking_consent: trackingConsent,
          source: SaveCartForm.consentLog.source,
          channel: 'email',
          // Cart value for the Supabase lead log (saved_cart_leads), not the
          // CNIL consent proof itself — n8n splits it into its own table.
          cart_total_cents: totalPriceShopCurrency,
          currency: cart.currency,
          item_count: cart.item_count,
          cart_permalink: cartPermalink,
          language_iso: this.languageIso,
        }),
      });
    } catch (error) {
      console.error('[save-cart-form] consent log failed', error?.message || String(error));
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // POST to the Klaviyo Client API, falling through the host list when a host
  // is unreachable (network TypeError — DNS/content blocker). An HTTP error
  // response is returned as-is and never retried on another host.
  async postToKlaviyo(path, body) {
    const init = {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: SaveCartForm.klaviyo.revision,
      },
      body: JSON.stringify(body),
    };
    let lastError = null;
    for (const host of SaveCartForm.klaviyo.hosts) {
      const url = new URL(`https://${host}${path}`);
      url.searchParams.set('company_id', SaveCartForm.klaviyo.companyId);
      try {
        return await fetch(url, init);
      } catch (error) {
        if (!SaveCartForm.isNetworkFailure(error)) throw error;
        console.warn(`[save-cart-form] ${host} unreachable (blocked?), trying next host`, error?.message || String(error));
        lastError = error;
      }
    }
    throw lastError || new TypeError('Klaviyo unreachable');
  }

  onSuccess(cart) {
    this.form.classList.add('hidden');
    this.successElement?.classList.remove('hidden');
    const detail = {
      language_iso: this.languageIso,
      source: SaveCartForm.klaviyo.source,
      currency: cart?.currency || '',
      value: Math.round(cart?.total_price || 0) / 100,
      item_count: cart?.item_count || 0,
    };
    window.dispatchEvent(new CustomEvent('save-cart:success', { detail }));
    this.pushDataLayer(detail);
  }

  // GA4 recommended `generate_lead` event via GTM's dataLayer — same shape the
  // bottom-sheet pushes (language / market / device_type), plus lead_source so
  // GTM can tell this form apart from the popup and bottom-sheet leads.
  pushDataLayer(detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'generate_lead',
        lead_source: 'save_cart',
        currency: detail.currency,
        value: detail.value,
        item_count: detail.item_count,
        language: window.Shopify?.locale || document.documentElement.lang || null,
        market: window.Shopify?.country || null,
        device_type: window.matchMedia('(max-width: 639px)').matches ? 'mobile' : 'desktop',
      });
    } catch (error) {
      console.warn('[save-cart-form] dataLayer push skipped', error?.message || String(error));
    }
  }

  setError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.classList.toggle('hidden', message === '');
  }
}

customElements.define('save-cart-form', SaveCartForm);
